import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import exclamatoryGreen from "../images/exclamatoryGreen.png";
import closeIconImage from "../images/popup_close.png";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "../styles/AcceptUserPopup.css";

const formatDate = (date) => {
  if (!date) return "";
  const options = { day: "2-digit", month: "short", year: "numeric" };
  return date.toLocaleDateString("en-GB", options);
};

const parseDate = (value) => {
  const [day, month, year] = value.split(" ");
  const date = new Date(`${month} ${day}, ${year}`);
  return isNaN(date.getTime()) ? null : date;
};

const formatDateForAPI = (date) => {
  return date ? date.toISOString().split("T")[0] : "";
};

const AcceptUserPopup = ({
  open,
  userProfile,
  licenseKey,
  onLicenseKeyChange,
  onClose,
  onApprove,
}) => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [startDateText, setStartDateText] = useState("");
  const [endDateText, setEndDateText] = useState("");
  const [showStartCalendar, setShowStartCalendar] = useState(false);
  const [showEndCalendar, setShowEndCalendar] = useState(false);
  const startCalendarRef = useRef(null);
  const endCalendarRef = useRef(null);

  const isApproveDisabled =
    userProfile === "Teacher" && (!licenseKey.trim() || !startDate || !endDate);

  const handleStartDateChange = (date) => {
    setStartDate(date);
    setStartDateText(formatDate(date));
    // console.log("startdate:", startDate);
    setShowStartCalendar(false);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
    setEndDateText(formatDate(date));
    setShowEndCalendar(false);
  };

  const handleApproveClick = () => {
    onApprove(formatDateForAPI(startDate), formatDateForAPI(endDate)); // Send formatted dates
  };

  const handleOutsideClick = (event) => {
    if (
      startCalendarRef.current &&
      !startCalendarRef.current.contains(event.target)
    ) {
      setShowStartCalendar(false);
    }
    if (
      endCalendarRef.current &&
      !endCalendarRef.current.contains(event.target)
    ) {
      setShowEndCalendar(false);
    }
  };

  const handleDateInput = (value, setDate, setDateText) => {
    setDateText(value);
    const date = parseDate(value);
    if (date) setDate(date);
  };

  // Reset form fields when the popup is closed or canceled
  const resetForm = () => {
    setStartDate(null);
    setEndDate(null);
    setStartDateText("");
    setEndDateText("");
    onLicenseKeyChange(""); // Reset the license key as well
  };

  // When the popup opens or closes, reset form data if it's closed
  useEffect(() => {
    if (open) {
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [open]);

  if (!open) return null;

  return (
    <div className="dialog-overlay">
      <div
        className={`dialog ${
          userProfile === "Teacher" ? "dialog-teacher" : "dialog-student"
        }`}
      >
        <div className="dialog-header">
          <img
            src={closeIconImage}
            alt="Close"
            onClick={() => {
              onClose();
              resetForm();
            }} // Reset form on close
            className="close-icon"
            aria-label="Close dialog"
          />
        </div>
        <div className="report-problem-section">
          <img
            src={exclamatoryGreen}
            alt="Report Problem"
            className="report-problem-icon"
          />
          <div className="dialog-title">
            You are about to approve a {userProfile}
          </div>
          <p className="dialog-subtitle">
            Are you sure you want to approve this {userProfile}? This action
            cannot be undone.
          </p>
        </div>

        {userProfile === "Teacher" && (
          <div className="license-key-input-section">
            <input
              type="text"
              id="licenseKey"
              placeholder="License"
              value={licenseKey}
              onChange={(e) => onLicenseKeyChange(e.target.value)}
              required
            />
            <label htmlFor="licenseKey">License Key</label>

            <div className="custom-calendar">
              <input
                type="text"
                placeholder="Start Date (DD MMM YYYY)"
                value={startDateText}
                onChange={(e) =>
                  handleDateInput(
                    e.target.value,
                    setStartDate,
                    setStartDateText
                  )
                }
                onClick={() => setShowStartCalendar(!showStartCalendar)}
              />
              <label>Start Date</label>
              {showStartCalendar && (
                <div className="calendar-popup" ref={startCalendarRef}>
                  <Calendar
                    onChange={handleStartDateChange}
                    value={startDate}
                  />
                </div>
              )}
            </div>

            <div className="custom-calendar">
              <input
                type="text"
                placeholder="End Date (DD MMM YYYY)"
                value={endDateText}
                onChange={(e) =>
                  handleDateInput(e.target.value, setEndDate, setEndDateText)
                }
                onClick={() => setShowEndCalendar(!showEndCalendar)}
              />
              <label>End Date</label>
              {showEndCalendar && (
                <div className="calendar-popup" ref={endCalendarRef}>
                  <Calendar
                    onChange={handleEndDateChange}
                    value={endDate}
                    minDate={new Date()} // Restrict end date to current date or future
                  />
                </div>
              )}
            </div>
          </div>
        )}

        <div className="dialog-actions">
          <button
            onClick={() => {
              onClose();
              resetForm();
            }}
            className="cancel-button"
          >
            Cancel
          </button>
          <button
            onClick={handleApproveClick}
            className="approve-button"
            aria-label="approve user"
            disabled={isApproveDisabled}
            style={{
              cursor: isApproveDisabled ? "not-allowed" : "pointer",
              opacity: isApproveDisabled ? 0.6 : 1,
            }}
          >
            Approve
          </button>
        </div>
      </div>
    </div>
  );
};

AcceptUserPopup.propTypes = {
  open: PropTypes.bool.isRequired,
  userProfile: PropTypes.string,
  licenseKey: PropTypes.string,
  onLicenseKeyChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onApprove: PropTypes.func.isRequired,
};

export default AcceptUserPopup;
